import React, { useState } from 'react';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { Receipt, CheckCircle, XCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../../components/ui/alert';

const BoletoPay = () => {
  const [barcode, setBarcode] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);

    try {
      const response = await api.post('/me/boleto/pay', {
        barcode,
        amount: parseFloat(amount),
      });

      setResult(response.data);
      
      if (response.data.success) {
        toast({
          title: 'Boleto pago!',
          description: 'Pagamento realizado com sucesso.',
        });
        setBarcode('');
        setAmount('');
      } else {
        toast({
          variant: 'destructive',
          title: 'Falha no pagamento',
          description: 'Não foi possível completar o pagamento.',
        });
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao pagar boleto',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="boleto-pay-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Pagar Boleto</h1>
        <p className="text-muted-foreground mt-2">Realize pagamentos de boletos bancários</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle>Novo Pagamento</CardTitle>
            <CardDescription>Digite o código de barras do boleto</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="barcode">Código de Barras</Label>
                <Input
                  id="barcode"
                  type="text"
                  placeholder="00000.00000 00000.000000 00000.000000 0 00000000000000"
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  required
                  className="h-11 font-mono"
                  data-testid="boleto-barcode-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount">Valor</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  className="h-11 font-mono"
                  data-testid="boleto-amount-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11 rounded-full"
                disabled={loading}
                data-testid="boleto-pay-button"
              >
                <Receipt className="mr-2 h-5 w-5" strokeWidth={1.5} />
                {loading ? 'Processando...' : 'Pagar Boleto'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {result && (
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {result.success ? (
                  <CheckCircle className="h-5 w-5 text-success" strokeWidth={1.5} />
                ) : (
                  <XCircle className="h-5 w-5 text-destructive" strokeWidth={1.5} />
                )}
                {result.success ? 'Pagamento Confirmado' : 'Pagamento Falhou'}
              </CardTitle>
              <CardDescription>ID: {result.payment_id}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant={result.success ? 'default' : 'destructive'}>
                <AlertDescription>
                  {result.success
                    ? 'Seu boleto foi pago com sucesso!'
                    : 'Não foi possível completar o pagamento. Tente novamente.'}
                </AlertDescription>
              </Alert>

              <div className="rounded-lg bg-muted/50 p-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Código de Barras:</span>
                  <span className="font-mono text-xs">{result.barcode.slice(0, 20)}...</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Valor:</span>
                  <span className="font-bold font-mono">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(result.amount)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Status:</span>
                  <span className={`font-medium ${
                    result.status === 'CONFIRMED' ? 'text-success' : 'text-destructive'
                  }`}>
                    {result.status === 'CONFIRMED' ? 'Confirmado' : 'Falhou'}
                  </span>
                </div>
                {result.processed_at && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Processado em:</span>
                    <span className="font-medium">
                      {new Date(result.processed_at).toLocaleString('pt-BR')}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BoletoPay;